<template>
  <ValidationObserver
    ref="observer"
    v-slot="{ validate, invalid, errors: observerErrors }"
    tag="div"
  >
    <b-modal ref="modal" size="xl" centered @hidden="hidden">
      <div slot="modal-header">
        <div class="modal-header__hide" @click="hideModal">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
            <path
              d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
            />
            <path d="M0 0h24v24H0z" fill="none" />
          </svg>
        </div>
        <h2 class="h1-like">{{ $t("add_campaign.add_new_campaign") }}</h2>
        <div class="modal-header__description">
          {{ $t("add_campaign.change_anytime") }}
        </div>
      </div>
      <div slot="modal-footer" class="d-flex flex-column">
        <b-alert
          v-if="!gateways.length"
          show
          variant="warning"
          class="w-100 d-flex flex-column align-items-center"
        >
          {{ $t("add_campaign.no_gateway_alert", "To create campaign please add gateway") }}
          <router-link to="/gateways?i=1">
            {{ $t("add_campaign.add_gateway", "Add gateway") }}
          </router-link>
        </b-alert>
        <div class="w-100 d-flex justify-content-between">
          <a href="#" class="bttn bttn--lg bttn--blue" @click="hideModal">
            {{ $t("add_campaign.close") }}
          </a>
          <button
            form="campaign-add-express"
            :disabled="invalid || sending || !gateways.length"
            class="bttn bttn--lg bttn--orange"
            type="submit"
          >
            {{ $t("add_campaign.create_new_campaign") }}
          </button>
        </div>
      </div>
      <form id="campaign-add-express" @submit.prevent="validate().then(handleSubmit)">
        <error
          v-if="$options.filterErrors(errors, observerErrors)"
          :errors="$options.filterErrors(errors, observerErrors)"
        />
        <div class="d-flex align-items-center justify-content-center">
          <image-upload v-model="newCampaign.image" type="campaign" label="Upload campaign image" />
        </div>
        <div class="row align-items-end">
          <div class="col-12 col-md-6">
            <ValidationProvider rules="no_whitespace" v-slot="{}">
              <div class="form-group form-unit">
                <label class="form-unit__label">
                  {{ $t("add_campaign.express_title") }}
                </label>
                <input
                  type="text"
                  class="form-control form-unit__input"
                  v-model="newCampaign.title"
                  required
                />
              </div>
            </ValidationProvider>
          </div>
          <div class="col-12 col-md-6">
            <ValidationProvider rules="short_link" v-slot="{}">
              <div class="form-group form-unit form-unit--addon date-time-select">
                <label class="form-unit__label">
                  {{ $t("edit_campaign.generalinfo_short_link") }}
                </label>
                <div class="form-unit__addon-wrap">
                  <div class="form-unit__addon-icon form-unit__addon-icon--wide">charidy.com/</div>
                  <input
                    type="text"
                    required
                    class="form-control form-unit__input"
                    :placeholder="$t('edit_campaign.generalinfo_short_link')"
                    v-model="newCampaign.short_link"
                  />
                </div>
              </div>
            </ValidationProvider>
          </div>
        </div>
        <div class="row">
          <div class="col-12 col-md-6">
            <div class="form-group form-unit form-unit--addon goal-input">
              <label class="form-unit__label">
                {{ $t("add_campaign.currency") }}
              </label>
              <b-form-select v-model="newCampaign.currency" :options="currencies"></b-form-select>
            </div>
          </div>
          <div class="col-12 col-md-6">
            <div
              v-if="!loadingGateways && gateways.length"
              class="form-group form-unit form-unit--multiselect"
            >
              <label class="form-unit__label">
                {{ $t("add_campaign.gateway") }}
              </label>
              <div class="d-flex align-items-center">
                <multiselect
                  v-model="selectedGateways"
                  track-by="id"
                  :custom-label="customLabel"
                  :options="gateways"
                  :multiple="true"
                ></multiselect>
              </div>
            </div>
            <div v-else class="col-12 h-100 d-flex align-items-center justify-content-center">
              <b-spinner v-if="loadingGateways" small class="p-2"></b-spinner>
              <b-alert
                v-else
                show
                variant="warning"
                class="w-100 d-flex flex-column align-items-center"
              >
                {{ $t("add_campaign.not_gateways", "No active gateways") }}
                <router-link to="/gateways?i=1">
                  {{ $t("add_campaign.add_gateway", "Add gateway") }}
                </router-link>
              </b-alert>
            </div>
          </div>
        </div>
        <div class="row align-items-end">
          <div class="col-12">
            <label class="form-unit__label">
              {{ $t("edit_campaign.description_title") }}
            </label>
            <campign-description-form v-model="newCampaign.descriptions" />
          </div>
        </div>
      </form>
    </b-modal>
  </ValidationObserver>
</template>

<script>
import { mapState, mapActions } from "vuex";
import axios from "axios";
import urls from "@/constants/urls.js";
import Multiselect from "vue-multiselect";
import error from "@/components/errors.vue";
import campignDescriptionForm from "@/components/campign-description-form.vue";
import imageUpload from "@/components/image-upload.vue";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { filterErrors } from "@/helpers/filters";

export default {
  filterErrors,

  data() {
    return {
      newCampaign: {
        image: "",
        title: "",
        currency: "usd",
        short_link: "",
        descriptions: [],
      },
      selectedGateways: [],
      errors: [],
      sending: false,
    };
  },

  components: {
    error: error,
    campignDescriptionForm,
    Multiselect,
    imageUpload,
    ValidationObserver,
    ValidationProvider,
  },

  computed: {
    ...mapState({
      orgId: () => localStorage.getItem("orgId"),
      loadingGateways: (state) => state.gateways.loadingGateways,
      currencies: (state) =>
        state.account.currencyList.map((el) => ({
          value: el.attributes.code.toLowerCase(),
          text: `${el.attributes.title}(${el.attributes.code})`,
        })),
      gateways: (state) => state.gateways.gateways.filter((v) => v.attributes.active),
      locale: (state) => state.i18n.locale,
    }),
  },

  methods: {
    ...mapActions({
      fetchCreateExpressCampaign: "expressCampaign/fetchCreateExpressCampaign",
    }),

    customLabel({ attributes }) {
      return `${attributes.gateway}`;
    },

    hidden() {
      this.newCampaign = {
        image: "",
        title: "",
        short_link: "",
        descriptions: [],
      };
    },

    getMediaUrl() {
      const { orgId } = this;
      const mediaUrl = this.newCampaign.image;

      return new Promise(function (resolve) {
        if (mediaUrl instanceof File) {
          const formData = new FormData();

          formData.append("file", mediaUrl);

          axios.post(urls.loadImageQuill.replace(":OrgId", orgId), formData).then((response) => {
            resolve(response.data.src);
          });
        } else {
          resolve(mediaUrl);
        }
      });
    },

    async handleSubmit() {
      this.sending = true;
      const data = JSON.parse(JSON.stringify(this.newCampaign));

      data.image = await this.getMediaUrl();
      data.descriptions = data.descriptions.map((el) => ({
        language: el.short,
        content: el.content,
        tag: el.tag,
      }));
      data.gateway_id_list = this.selectedGateways.map((el) => Number(el.id));

      this.fetchCreateExpressCampaign([data]).then(({ data }) => {
        const cId = data.campaign_id;
        this.sending = false;
        this.$router.push({ path: `/campaign/${cId}` });
      });
    },

    openModal() {
      this.$refs.modal.show();
    },

    hideModal() {
      this.$refs.modal.hide();
    },
  },
};
</script>

<style lang="scss" scoped></style>
